/**
 * The vocabulary and the form furniture the two WATCHLISTS share.
 *
 * A vehicle plate and a person's face are different things to put on a list, but they are the
 * same DECISION: this identity, when a camera sees it, should interrupt somebody. The severity
 * ladder, the words describing each rung, and the shape of the fields therefore live here rather
 * than being written twice — two lists that drifted would have "Critical" mean one thing on the
 * vehicle report and another on the face report, and an operator would have no way to know.
 */
import type { ReactNode } from 'react';

/**
 * Severity of the alert a sighting raises.
 *
 * Tinted with the numeric Tailwind palette rather than the theme's status tokens: those are
 * bare `var()` colours, and Tailwind v3 emits NO rule at all for an opacity modifier on one of
 * those — `bg-warning/15` compiles to nothing. The reports' own badges use these same numeric
 * classes for the same reason.
 */
export const WATCHLIST_SEVERITIES: Array<{ value: string; label: string; chip: string; hint: string }> = [
  { value: 'critical', label: 'Critical', chip: 'bg-red-500/15 text-red-500', hint: 'Interrupt the operator. The default, and what a watchlist is for.' },
  { value: 'warning', label: 'Warning', chip: 'bg-amber-500/15 text-amber-500', hint: 'Raise it, but below the life-safety detections.' },
  { value: 'normal', label: 'Log only', chip: 'bg-blue-500/15 text-blue-500', hint: 'Recorded and visible, but it will not compete for attention.' },
];

export const watchlistSeverityOf = (value?: string | null) =>
  WATCHLIST_SEVERITIES.find((s) => s.value === value);

/** One labelled control. Both watchlists lay their fields out full width and stacked. */
export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export const WATCHLIST_INPUT = 'w-full h-9 rounded-lg border border-border bg-background px-3 text-[13px] '
  + 'text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none';

/**
 * ─── WHY A WATCHLIST ENTRY HAS A CATEGORY AS WELL AS A SEVERITY ───
 *
 * Severity says how loudly to shout. The category says WHAT THE OPERATOR IS LOOKING AT, and
 * the two are not the same question: a missing child and a barred ex-contractor can both be
 * critical, and the response to them has nothing in common. Until now every hit came out of
 * the evaluator named "Blacklisted Person", so the wall could not tell them apart and the
 * operator had to open the alert and read the reason to find out whether to call the police
 * or call reception.
 *
 * The category therefore NAMES THE ALERT (`alertName`), which is the whole point of it. It is
 * stored on the entry, not derived from the reason text, because a free-text reason cannot be
 * filtered, counted or routed.
 *
 * `blacklisted` is first and is the default in both lists: everything flagged before these
 * categories existed is a blacklist entry, and carrying it over as anything else would be
 * inventing a claim about people and vehicles somebody else listed.
 */
export interface WatchlistCategory {
  value: string;
  label: string;
  /** Outlined, where severity chips are filled — the two sit side by side on a row and a
   *  reader must be able to tell at a glance which of the two questions each answers. */
  chip: string;
  hint: string;
  /**
   * Tint for a filled banner, where the category is the whole message rather than one chip
   * among several. Red is the colour of a threat, and a missing person is not one — a banner
   * that said "Missing" in the red reserved for barred intruders would be telling the operator
   * the opposite of what the category means.
   */
  banner: string;
  /**
   * Bare text/icon colour, for the places where the category is spoken by something that is
   * not a box of its own — the dialog's title icon, a line of prose. Same hue as `banner`.
   */
  accent: string;
  /**
   * LEFT EDGE, for a row in a list that is already carrying chips of its own.
   *
   * Written out as a whole literal class rather than derived from `chip`, because Tailwind
   * only emits rules for class names it can SEE in the source: a class assembled at runtime
   * (`chip.replace(...)`) is scanned as nothing, so the rule never ships and the row renders
   * with no edge at all — silently, and only in a production build.
   */
  edge: string;
  /**
   * FILLED, for the one button that commits the entry. It carries the same hue as the banner
   * for the same reason the banner is not always red: the confirm button is the last thing
   * the operator reads before listing somebody, and a red one under a sky-blue "Missing"
   * banner is the dialog contradicting itself about what is being done.
   */
  solid: string;
  /** What the alert is called when this entry is matched. See alertEvaluator. */
  alertName: string;
}

export const PERSON_WATCHLIST_CATEGORIES: WatchlistCategory[] = [
  {
    value: 'blacklisted',
    label: 'Blacklisted',
    chip: 'border border-red-500/40 text-red-500',
    banner: 'bg-red-500/10 text-red-500',
    edge: 'border-l-red-500',
    accent: 'text-red-500',
    solid: 'bg-red-500 hover:bg-red-600',
    hint: 'Barred from site. A sighting is a security response.',
    alertName: 'Blacklisted Person',
  },
  {
    value: 'wanted',
    label: 'Wanted',
    chip: 'border border-violet-500/40 text-violet-500',
    banner: 'bg-violet-500/10 text-violet-500',
    edge: 'border-l-violet-500',
    accent: 'text-violet-500',
    solid: 'bg-violet-500 hover:bg-violet-600',
    hint: 'Sought by the police or by an investigation. A sighting is a report to make.',
    alertName: 'Wanted Person',
  },
  {
    value: 'missing',
    label: 'Missing',
    chip: 'border border-sky-500/40 text-sky-500',
    banner: 'bg-sky-500/10 text-sky-500',
    edge: 'border-l-sky-500',
    accent: 'text-sky-500',
    solid: 'bg-sky-500 hover:bg-sky-600',
    hint: 'Reported missing. A sighting is a lead to follow, not a threat to repel.',
    alertName: 'Missing Person',
  },
];

export const VEHICLE_WATCHLIST_CATEGORIES: WatchlistCategory[] = [
  {
    value: 'blacklisted',
    label: 'Blacklisted',
    chip: 'border border-red-500/40 text-red-500',
    banner: 'bg-red-500/10 text-red-500',
    edge: 'border-l-red-500',
    accent: 'text-red-500',
    solid: 'bg-red-500 hover:bg-red-600',
    hint: 'Barred from site. A sighting is a security response.',
    alertName: 'Blacklisted Vehicle',
  },
  {
    value: 'stolen',
    label: 'Stolen',
    chip: 'border border-violet-500/40 text-violet-500',
    banner: 'bg-violet-500/10 text-violet-500',
    edge: 'border-l-violet-500',
    accent: 'text-violet-500',
    solid: 'bg-violet-500 hover:bg-violet-600',
    hint: 'Reported stolen. A sighting is a report to the police, whoever is driving it.',
    alertName: 'Stolen Vehicle',
  },
];

export const DEFAULT_WATCHLIST_CATEGORY = 'blacklisted';

/**
 * The category for a stored value, or undefined.
 *
 * Tolerant on purpose. The vehicle list's `category` was FREE TEXT before this vocabulary
 * existed (migration 052) and sites have typed their own words into it, so a lookup must
 * accept "Stolen Vehicle", "stolen_vehicle" and "stolen" as the same rung and simply miss on
 * anything else — a miss renders the operator's own word verbatim rather than losing it.
 */
export function watchlistCategoryOf(list: WatchlistCategory[], value?: string | null) {
  if (!value) return undefined;
  const key = String(value).toLowerCase().replace(/[\s_-]+/g, ' ').trim();
  return list.find((c) => c.value === key
    || key === `${c.value} person` || key === `${c.value} vehicle`);
}

/** Label for a stored value: the vocabulary's word, or the operator's own if it predates it. */
export function watchlistCategoryLabel(list: WatchlistCategory[], value?: string | null) {
  return watchlistCategoryOf(list, value)?.label || value || null;
}

/**
 * The category picker both watchlists use.
 *
 * Segmented rather than a `<select>`, unlike severity: there are only two or three rungs, and
 * this is the field that decides what the alert will be CALLED, so it should be readable
 * without opening anything. The alert name is spelled out under the row for the same reason —
 * an operator choosing "Missing" should see that the wall will say "Missing Person".
 */
export function WatchlistCategoryPicker({
  categories, value, onChange, disabled,
}: {
  categories: WatchlistCategory[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const active = watchlistCategoryOf(categories, value);
  return (
    <div>
      <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${categories.length}, minmax(0, 1fr))` }}>
        {categories.map((c) => {
          const on = active?.value === c.value;
          return (
            <button
              key={c.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(c.value)}
              className={`h-9 rounded-lg px-2 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                on
                  ? `${c.chip} ${c.banner} font-semibold`
                  : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      {/* An unrecognised stored value is shown rather than silently re-pointed at the default:
          the entry really does say that, and an operator editing it must see what they are
          about to overwrite. */}
      {!active && value && (
        <p className="mt-1.5 text-[11px] text-amber-500">
          Currently listed as “{value}”. Choosing one of the above replaces it.
        </p>
      )}
    </div>
  );
}

/**
 * The severity ladder as a row rather than a `<select>`.
 *
 * It was a dropdown while it was the only control of its kind on the form. Next to a
 * segmented category picker it read as the lesser question, which is backwards — severity is
 * what decides whether the operator is interrupted — and a closed dropdown hides two thirds
 * of a ladder whose whole meaning is the comparison between its rungs.
 *
 * `disabled` is used, not hidden: with alerting off there IS no severity, and a row that
 * vanished would leave the operator unsure whether the setting had been lost.
 */
export function WatchlistSeverityPicker({
  value, onChange, disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className={`grid grid-cols-3 gap-1.5 ${disabled ? 'pointer-events-none opacity-50' : ''}`}
      aria-disabled={disabled || undefined}
    >
      {WATCHLIST_SEVERITIES.map((s) => {
        const on = s.value === value;
        return (
          <button
            key={s.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(s.value)}
            className={`h-9 rounded-lg px-2 text-[13px] transition-colors ${
              on
                ? `${s.chip} font-semibold`
                : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── The Watch List page's URL contract ────────────────────────────────────
//
// Here rather than in the page because the DASHBOARD builds these links — its two watch-list
// cards lead to the sightings behind them — and a component importing a page to get a query
// string is a dependency the wrong way round. Both sides already import this file for the
// category vocabulary, so this is where the two agree on everything else about a watch list too.

/** Query parameter names, in one place — `watchListHref` writes them, the page reads them. */
export const WATCHLIST_PARAMS = { tab: 'tab', subject: 'subject', window: 'window' } as const;

export type WatchListTab = 'people' | 'vehicles' | 'detections';

/**
 * A link INTO the Watch List page, at a given tab and filter.
 *
 * Exported so a caller does not have to know the parameter names or guess the window keys. A
 * link built by hand elsewhere is one that keeps working while silently landing on the wrong tab
 * the day one of these is renamed.
 *
 * @param base the route the page is mounted at, which differs per app
 */
export function watchListHref(
  base: string,
  opts: { tab?: WatchListTab; subject?: 'all' | 'person' | 'vehicle'; window?: string } = {},
): string {
  const q = new URLSearchParams();
  if (opts.tab) q.set(WATCHLIST_PARAMS.tab, opts.tab);
  if (opts.subject && opts.subject !== 'all') q.set(WATCHLIST_PARAMS.subject, opts.subject);
  if (opts.window) q.set(WATCHLIST_PARAMS.window, opts.window);
  const qs = q.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Which picture best identifies a sighting — a key of the event's `image_urls`, or null.
 *
 * A KEY rather than a URL, because the two apps turn it into pixels differently: a node loads
 * the URL straight out of the map, and the cloud passes the key to its tunnel as the image TYPE
 * to stream back. One choice, two transports — the same split `crop_key` uses on the search
 * results.
 *
 * The order is what identifies the subject, narrowest first. A watch-list sighting answers "is
 * that them?", and at thumbnail size a whole car park does not: the face, then the person, then
 * the scene. A vehicle has no face, so it starts at its own crop and ends at the plate, which is
 * the one picture that settles a plate match.
 *
 * Spelling-tolerant because the AI module names the same crop three ways across models — see
 * modelEventsController's IMAGE_SYNONYMS, which this deliberately mirrors rather than assumes
 * away.
 */
const DETECTION_IMAGE_PREFERENCE: Record<'person' | 'vehicle', string[]> = {
  person: ['faceCrop', 'alignedFace', 'personCrop', 'fullFrame', 'snapshot'],
  vehicle: ['vehicleCrop', 'objectCrop', 'plateCrop', 'fullFrame', 'snapshot'],
};

export function detectionImageKey(
  detection: { kind?: string; image_urls?: Record<string, string> | null } | null | undefined,
): string | null {
  const urls = detection?.image_urls;
  if (!urls || typeof urls !== 'object') return null;
  const flat = (k: string) => k.toLowerCase().replace(/[^a-z0-9]/g, '');
  const keys = Object.keys(urls).filter((k) => urls[k]);

  for (const want of DETECTION_IMAGE_PREFERENCE[detection?.kind === 'vehicle' ? 'vehicle' : 'person']) {
    const w = flat(want);
    // Exact first, then PREFIX — the per-subject crops are numbered (`vehicle_crop_0`,
    // `personCrop2`), so a frame holding several vehicles names none of them plainly
    // `vehicleCrop` and an exact-only match falls all the way through to the wide shot. Exact
    // still wins where both exist, so `faceCrop` is never beaten by a `faceCropAligned`.
    const hit = keys.find((k) => flat(k) === w) || keys.find((k) => flat(k).startsWith(w));
    if (hit) return hit;
  }
  // Something is better than the letter avatar, and a module that names its one frame something
  // nobody has seen yet should not cost the operator the picture.
  return keys[0] || null;
}
